import type {CmLogger} from "@cm/log"
import type {CompactUIDTable} from "@cm/utils"
import type {Matrix4} from "@cm/math"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {InterfaceDescriptor} from "#template/interface-descriptors"
import type {MaterialLikeNode} from "#template/nodes/types"
import type {MaterialAssignments} from "#template/nodes/material-assignment/material-assignments"
import type {MeshDecal} from "#template/nodes/object/mesh-decal"
import type {Render} from "#template/nodes/render/render"
import type {SceneProperties} from "#template/nodes/render/scene-properties"
import type {Camera} from "#template/nodes/object/camera"
import type {PostProcessRender} from "#template/nodes/render/post-process-render"
import type {HdriLight} from "#template/nodes/render/hdri-light"
import type {TemplateGraph} from "#template/nodes/template-graph/template-graph"
import type {TemplateInstance} from "#template/nodes/object/template-instance"
import type {BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {LiveValueAccessorListEntry, TransformAccessorListEntry} from "#template/runtime-graph/nodes/compile-template"
import type {SolverData} from "#template/runtime-graph/nodes/solver/data"
import type {SolverObjectData} from "#template/runtime-graph/nodes/solver/object-data"
import type {SolverRelationData} from "#template/runtime-graph/nodes/solver/relation-data"
import type {SolverVariableData} from "#template/runtime-graph/nodes/solver/variable-data"
import type {EvaluatedTemplateOutputs, TemplateOutputBuilders} from "#template/fundamental/evaluated-input"
import type {ObjectBounds} from "#template/geometry-processing/bounds"
import type {InfoNodes, InfoNodeId} from "#template/interfaces/info-nodes"
import type {ObjectPath} from "#template/template-node/types"
import type {TemplateNode} from "#template/template-node/template-node"
import type {ObjectPathPart} from "#template/interactions"
import type {LodTypeValue} from "#template/value-types"

export type TemplateData = {
    graph: TemplateGraph
}

// Differences/snapshot specialized over the TemplateNode tree.
// Use `ReturnType` over the underlying graph-node methods so the generic constraints stay in sync with v3
// (v3 parameterizes Snapshot/Differences over BaseGraphNode, not TemplateNode).
export type TemplateGraphSnapshot = ReturnType<TemplateGraph["getSnapshot"]>
export type TemplateGraphDifferences = ReturnType<TemplateGraph["getDifferences"]>

export type TemplateContext = {
    sceneManager: ISceneManager
    templateMatrix: Matrix4
    lodType: LodTypeValue
    computeDummyReifiedMeshData: boolean
    computeDataObjectReferences: boolean
    defaultCustomerId?: number
    showDimensions: boolean
    instanceIdChain: string[]
    logger: CmLogger
    localIds: CompactUIDTable<string>
}

export type ContextOtherData = Record<string, unknown>

export type OnCompileContextData = {
    evaluator: NodeEvaluator
    root: TemplateGraph
    sceneProperties: SceneProperties | undefined
    render: Render | undefined
    camera: Camera | undefined
    postProcessRender: PostProcessRender | undefined
    environment: HdriLight | undefined
    templateDepth: number
    path: ObjectPath
    overrideMaterial?: (parent: MeshDecal | {materialAssignments: MaterialAssignments; key: string}) => MaterialLikeNode | null | undefined
    currentTemplate: {
        preDisplayList: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[] | null>[]
        displayList: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[] | null>[]
        descriptorList: BuilderOutlet<InterfaceDescriptor<unknown>>[]
        infoList: BuilderOutlet<InfoNodes.InfoNode | null>[]
        solverData: {
            solverObjects: BuilderOutlet<SolverObjectData>[]
            solverVariables: BuilderOutlet<SolverVariableData>[]
            solverRelations: BuilderOutlet<SolverRelationData | null>[]
        }
        transformAccessorList: BuilderOutlet<TransformAccessorListEntry>[]
        liveValueAccessorList: BuilderOutlet<LiveValueAccessorListEntry>[]
        allBounds: BuilderOutlet<ObjectBounds>[]
        templateOutputs: TemplateOutputBuilders
        outputAffectedObjectPathParts: Map<string, {parts: ObjectPathPart[]; reExportIds: string[]}>
        nodeToInfoMap: Map<TemplateNode, InfoNodeId>
    }
    subTemplates: {
        readyList: BuilderOutlet<boolean>[]
        preDisplayLists: BuilderOutlet<SceneNodes.SceneNode[]>[]
        displayLists: BuilderOutlet<SceneNodes.SceneNode[]>[]
        exposeClaimedSubTemplateInputs: boolean
        descriptorLists: BuilderOutlet<InterfaceDescriptor<unknown>[]>[]
        subTemplatesOutputs: BuilderOutlet<EvaluatedTemplateOutputs>[]
        transformAccessorLists: BuilderOutlet<TransformAccessorListEntry[]>[]
        liveValueAccessorLists: BuilderOutlet<LiveValueAccessorListEntry[]>[]
        infoLists: BuilderOutlet<InfoNodes.InfoNode[]>[]
        solverDatas: BuilderOutlet<SolverData>[]
        lookupByExternalIdPathMap: Map<TemplateInstance, BuilderOutlet<(x: string[]) => TemplateNode | null>>
        inputSourceMappings: {mapping: Map<string, TemplateNode[]>; rawDescriptorList: BuilderOutlet<InterfaceDescriptor<unknown>[]>; nodeId: string}[]
        nodeToInfoMaps: BuilderOutlet<Map<TemplateNode, InfoNodeId>>[]
    }
    otherData?: ContextOtherData
}

export const getEmptyCompileContextData = (
    evaluator: NodeEvaluator,
    root: TemplateGraph,
    sceneProperties: SceneProperties | undefined,
    render: Render | undefined,
    camera: Camera | undefined,
    postProcessRender: PostProcessRender | undefined,
    environment: HdriLight | undefined,
    templateDepth: number,
    path: ObjectPath,
    exposeClaimedSubTemplateInputs: boolean,
    overrideMaterial?: (parent: MeshDecal | {materialAssignments: MaterialAssignments; key: string}) => MaterialLikeNode | null | undefined,
    contextOtherData?: ContextOtherData,
): OnCompileContextData => {
    return {
        evaluator,
        root,
        sceneProperties,
        render,
        camera,
        postProcessRender,
        environment,
        templateDepth,
        path,
        overrideMaterial,
        currentTemplate: {
            preDisplayList: [],
            displayList: [],
            descriptorList: [],
            infoList: [],
            solverData: {
                solverObjects: [],
                solverVariables: [],
                solverRelations: [],
            },
            transformAccessorList: [],
            liveValueAccessorList: [],
            allBounds: [],
            templateOutputs: {},
            outputAffectedObjectPathParts: new Map(),
            nodeToInfoMap: new Map(),
        },
        subTemplates: {
            readyList: [],
            preDisplayLists: [],
            displayLists: [],
            exposeClaimedSubTemplateInputs,
            descriptorLists: [],
            subTemplatesOutputs: [],
            transformAccessorLists: [],
            liveValueAccessorLists: [],
            infoLists: [],
            solverDatas: [],
            lookupByExternalIdPathMap: new Map(),
            inputSourceMappings: [],
            nodeToInfoMaps: [],
        },
        otherData: contextOtherData,
    }
}
